import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Card, CardHeader, CardContent, Button } from '@material-ui/core';
import localforage from 'localforage';
import PropTypes from 'prop-types';
import styles from 'styles/pages/fiches-techniques-brand.module.scss';
import { urlWriter } from 'tools/functions';
import getBrandsModelsSimple from 'lib/getBrandsModelsSimple';
import Breadcrumb from 'components/breadcrumb';
import { actionGetBrandsModels, actionSetGetBrandsModelsToNull } from 'store/actions';
import Link from 'components/link';

const WidgetNav = dynamic(() => import('../../../components/widgetNav'), {
    ssr: false,
});

const WidgetLaunches = dynamic(() => import('../../../components/widgetLaunches'), {
    ssr: false,
});

const WidgetPromo = dynamic(() => import('../../../components/widgetPromotion'), {
    ssr: false,
});

const FichesTechniquesBrands = (props) => {
    const { brand, dataGetBrandsModels } = props;
    const [brands, setBrands] = useState(null);

    useEffect(() => {
        localforage
            .getItem('brandsModels')
            .then((value) => {
                if (value) {
                    if (value.expire > new Date().getTime()) {
                        setBrands(value.data);
                    } else {
                        localforage.removeItem('brandsModels');
                    }
                } else {
                    props.actionGetBrandsModels();
                }
            })
            .catch(() => {
                props.actionGetBrandsModels();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (dataGetBrandsModels) {
            setBrands(dataGetBrandsModels);
            const now = new Date();
            localforage.setItem('brandsModels', {
                data: dataGetBrandsModels,
                expire: now.setDate(now.getDate() + 3),
            });
            props.actionSetGetBrandsModelsToNull();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataGetBrandsModels]);

    return (
        <div>
            <Head>
                <title>
                    {`Fiches techniques ${brand.brand} neuve au Maroc | soeezauto.ma`}
                </title>
                <meta
                    name="description"
                    content={`Fiches techniques modeles ${brand.brand} neuve au Maroc, guide d'achat, prix, comparateur`}
                />
                <meta property="og:type" content="siteweb" />
                <meta
                    property="og:title"
                    content={`Fiche technique ${brand.brand} neuve au Maroc, prix`}
                />
                <meta
                    property="og:image"
                    content={`${process.env.NEXT_PUBLIC_API_HOST}/images/brands/${brand.image}`}
                />
                <meta
                    property="og:url"
                    content={`${
                        process.env.NEXT_PUBLIC_CLIENT_HOST
                    }/fiches-techniques/${urlWriter(brand.brand)}`}
                />
                <link
                    rel="canonical"
                    href={`${
                        process.env.NEXT_PUBLIC_CLIENT_HOST
                    }/fiches-techniques/${urlWriter(brand.brand)}`}
                />
            </Head>

            <main className={styles.root}>
                <Breadcrumb
                    links={[
                        {
                            href: '/fiches-techniques',
                            text: 'Fiches techniques',
                        },
                        {
                            href: null,
                            text: brand.brand,
                        },
                    ]}
                />
                <div className="main-title">
                    <h1>{`Fiches techniques - ${brand.brand}`}</h1>
                </div>
                {brand && (
                    <div className={styles.mainContainer}>
                        <Card className={styles.cardRoot}>
                            <CardHeader
                                title={<h2>Modèles</h2>}
                                avatar={
                                    <Link
                                        href={`/marques-voiture/${brand.brand.replace(
                                            / /g,
                                            '-',
                                        )}`}
                                    >
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_API_HOST}/images/brands/${brand.image}`}
                                            alt={`${brand.brand}`}
                                            width="100"
                                            height="100"
                                            loading="eager"
                                            priority
                                        />
                                    </Link>
                                }
                            />
                            <CardContent className={styles.cardContent}>
                                {brand.models.map((model, index) => (
                                    <Box key={model.model}>
                                        <Link
                                            href={`/fiches-techniques/${urlWriter(
                                                brand.brand,
                                            )}/${urlWriter(model.model)}`}
                                        >
                                            <Button
                                                data-modelindex={index}
                                                id={model.id}
                                                className={styles.range}
                                                variant="contained"
                                            >
                                                {model.model}
                                            </Button>
                                        </Link>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                )}
                {brands && (
                    <>
                        <WidgetPromo data={brands} />
                        <WidgetNav brands={brands} />
                        <WidgetLaunches data={brands} />
                    </>
                )}
            </main>
        </div>
    );
};

FichesTechniquesBrands.propTypes = {
    brand: PropTypes.any,
    actionGetBrandsModels: PropTypes.func.isRequired,
    actionSetGetBrandsModelsToNull: PropTypes.func.isRequired,
    dataGetBrandsModels: PropTypes.any,
};

const mapStateToProps = (state) => {
    return {
        dataGetBrandsModels: state.brand.dataGetBrandsModels,
    };
};

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            actionGetBrandsModels,
            actionSetGetBrandsModelsToNull,
        },
        dispatch,
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(FichesTechniquesBrands);

export async function getStaticPaths() {
    let brands = await getBrandsModelsSimple();
    brands = brands.data.brands;
    const paths = [];
    brands.forEach((brand) => {
        paths.push({
            params: {
                brand: urlWriter(brand.brand),
            },
        });
    });

    return {
        paths,
        fallback: false,
    };
}

export async function getStaticProps({ params }) {
    const brandParam = params.brand;
    let brands = await getBrandsModelsSimple();
    brands = brands.data.brands;
    const brandFilter = brands.filter((brand) => {
        return brand.brand.toLowerCase() === brandParam.toLowerCase().replace(/-/g, ' ');
    });
    if (brandFilter.length === 0) {
        return {
            notFound: true,
        };
    }
    return {
        props: {
            brand: brandFilter[0],
        },
    };
}
